/**
 * Tooltip — pełna treść przy hover LUB focusie klawiatury (dostępność).
 *
 * Zbudowany na Radix (`@radix-ui/react-tooltip`) zamiast ręcznego portalu:
 *  - trigger reaguje też na FOCUS z klawiatury (poprzednia wersja: tylko mysz),
 *  - poprawne role/aria (`aria-describedby` na triggerze, `role="tooltip"`),
 *  - auto-flip przy braku miejsca + portal (ucieka z `overflow:hidden` rodziców),
 *  - domyślnie chowa się przy scrollu/klawiszu Esc.
 *
 * Zastępuje natywne `title={...}` (szare, niestylowane, bez multi-line).
 * API zachowane 1:1 z poprzednią wersją — `content` pusty = no-op (zwraca dziecko).
 *
 * @example
 * <Tooltip content="Administrator, Księgowa, Tylko odczyt">
 *   <span className="truncate">Administrator, Księgowa, +1</span>
 * </Tooltip>
 */

import { type ReactElement, type ReactNode, useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

/**
 * Wariant kolorystyczny tooltipu (parytet z Badge `tone` / AlertElement):
 *  - `default` — neutralny (tło elevated, jak dotąd),
 *  - `info` — niebieski (podpowiedzi, opisy elementów),
 *  - `success` / `warning` / `danger` — statusowe.
 */
export type TooltipTone = 'default' | 'info' | 'success' | 'warning' | 'danger';

/** Klasy pudełka + wypełnienia strzałki per tone (light + dark). */
// Strzałka (caret) dostaje kolor OBRAMOWANIA danego tonu (nie tła), żeby trójkąt był
// wyraźnie widoczny na jasnym tle (tło blue-50 byłoby prawie białe = niewidoczne).
const TONE_STYLES: Record<TooltipTone, { box: string; arrow: string }> = {
  default: {
    box: 'border-border-primary bg-bg-elevated text-text-primary',
    arrow: 'fill-border-primary',
  },
  info: {
    box: 'border-blue-400 bg-blue-100 text-blue-900 dark:border-blue-600 dark:bg-blue-900 dark:text-blue-100',
    arrow: 'fill-blue-100 dark:fill-blue-900',
  },
  success: {
    box: 'border-green-300 bg-green-50 text-green-900 dark:border-green-700 dark:bg-green-950 dark:text-green-100',
    arrow: 'fill-green-400 dark:fill-green-600',
  },
  warning: {
    box: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100',
    arrow: 'fill-amber-400 dark:fill-amber-600',
  },
  danger: {
    box: 'border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-100',
    arrow: 'fill-red-400 dark:fill-red-600',
  },
};

interface TooltipProps {
  /** Treść tooltip. Gdy pusta — tooltip się nie pokazuje. */
  content: ReactNode;
  /** Element-trigger (pojedynczy element przekazujący ref — np. <span>, ikona). */
  children: ReactElement;
  /** Opóźnienie hover→show w ms. Default 300. */
  delay?: number;
  /** Preferowana pozycja. Default 'top'. Radix flipuje automatycznie przy braku miejsca. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Wariant kolorystyczny. Default 'default' (neutralny). */
  tone?: TooltipTone;
  /** Pokaż strzałkę (caret) wskazującą trigger. Default true. */
  arrow?: boolean;
  /** Max szerokość tooltipu w px. Default 280. */
  maxWidth?: number;
  /** Dodatkowa klasa dla pudełka tooltipu. */
  className?: string;
  /**
   * Wyłącza dymek: nie otwiera się przy najechaniu ani fokusie. W odróżnieniu od pustego `content`
   * (które zwraca samo dziecko, czyli INNE drzewo komponentów) drzewo zostaje to samo — `Root` i
   * `Trigger` dalej owijają dziecko — więc przełączenie `disabled` nie montuje dziecka od nowa
   * (przycisk nie traci fokusu ani stanu). Użyj, gdy dymek ma się pojawiać warunkowo, np. tylko przy
   * uciętym tekście, a element jest przy tym interaktywny. Domyślnie `false`.
   */
  disabled?: boolean;
  /**
   * Tekst dla czytnika ekranu zamiast treści dymka. Trafia jako `aria-label` na `Content`: Radix
   * wstawia go wtedy do ukrytej kopii (`role="tooltip"`, na którą wskazuje `aria-describedby`
   * triggera) zamiast `content`, a widoczny dymek traci własną rolę. Użyj, gdy `content` powtarza
   * to, co czytnik ma już z nazwy triggera (np. dymek z pełną nazwą + podpowiedzią — czytnik
   * dostaje samą podpowiedź). Bez tej opcji czytnik dostaje `content`.
   */
  ariaLabel?: string;
}

export function Tooltip({
  content,
  children,
  delay = 300,
  placement = 'top',
  tone = 'default',
  arrow = true,
  maxWidth = 280,
  className,
  disabled = false,
  ariaLabel,
}: TooltipProps) {
  // Stan `open` trzymamy sami (`Root` jest zawsze KONTROLOWANY), żeby `disabled` mógł wymusić
  // zamknięcie bez przełączania `Root` między trybem kontrolowanym a niekontrolowanym (Radix źle to
  // znosi) i bez zmiany drzewa. Hooki przed wczesnym zwrotem — ich liczba nie zależy od `content`.
  const [open, setOpen] = useState(false);
  // Wyłączenie dymka zeruje stan (dopasowanie stanu do propsa w trakcie renderu): gdy `disabled`
  // znika, a kursor zdążył odejść, dymek nie może wyskoczyć sam — Radix nie woła
  // `onOpenChange(false)`, gdy kontrolowane `open` już jest `false`.
  if (disabled && open) setOpen(false);

  if (!content) {
    return children;
  }

  const toneStyle = TONE_STYLES[tone];

  return (
    <TooltipPrimitive.Provider delayDuration={delay}>
      <TooltipPrimitive.Root
        open={!disabled && open}
        onOpenChange={(next) => setOpen(!disabled && next)}
      >
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            aria-label={ariaLabel}
            side={placement}
            sideOffset={8}
            collisionPadding={8}
            style={{ maxWidth }}
            className={cn(
              'pointer-events-none z-[10001] rounded-md border px-2.5 py-1.5 text-xs shadow-lg',
              'whitespace-pre-line break-words',
              toneStyle.box,
              className,
            )}
          >
            {content}
            {arrow ? (
              <TooltipPrimitive.Arrow className={toneStyle.arrow} width={13} height={6} />
            ) : null}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
